import type { File as ReploFile } from "schemas/generated/file";

import React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { LabeledControl } from "@replo/design-system/blocks/labeled-control/LabeledControl";
import { SelectionItem } from "@replo/design-system/blocks/selection/SelectionItem";
import { Button } from "@replo/design-system/components/button/Button";
import { Input } from "@replo/design-system/components/input/Input";
import { Textarea } from "@replo/design-system/components/textarea/Textarea";
import { toast } from "@replo/design-system/components/toast/Toast";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { useEditorStoreActions } from "@v2/store/editorStore";
import {
  nextJSProjectRouter,
  nextJSProjectUtils,
} from "@v2/trpc/routes/nextJSProject";
import { shopMetadataRouter } from "@v2/trpc/routes/shopMetadata";
import { X } from "lucide-react";
import { Controller, useForm } from "react-hook-form";
import { ReploError } from "schemas/errors";
import { z } from "zod";

import useDefaultCustomDomain from "./useDefaultCustomDomain";

class OpenGraphImageUploadError extends ReploError {}

const basePageSettingsSchema = z.object({
  pageName: z.string().min(1, "Page name is required"),
  pageUrl: z
    .string()
    .transform((val) => val.trim())
    .pipe(
      z
        .string()
        .min(1, 'URL is required. Set path to "/" to set it as your home page')
        .regex(/^\//, 'Path must start with "/".')
        .regex(/^\S*$/, "Path cannot contain spaces.")
        // NOTE (goleary, 2026-01-12, REPL-21582): Client-side format validation
        // Must match backend isValidRoutePath which uses slugify
        // Valid format: segments can only contain a-z, 0-9, underscores, and single hyphens
        // No leading/trailing hyphens per segment, no consecutive hyphens
        .refine(
          (val) => {
            if (val === "/") {
              return true; // Root is valid
            }

            const segments = val.slice(1).split("/");

            return segments.every((segment) => {
              if (segment.length === 0) {
                return false; // Empty segments not allowed
              }

              // Must match slugified version: lowercase alphanumeric, hyphens, underscores only
              // No leading/trailing hyphens, no consecutive hyphens
              return /^[\d_a-z]+(-[\d_a-z]+)*$/.test(segment);
            });
          },
          {
            message:
              "Invalid page path. Segments must contain only lowercase letters, numbers, hyphens, and underscores. Hyphens cannot be at the start, end, or consecutive.",
          },
        ),
    ),
  pageTitle: z
    .string()
    .max(1000, "Page title must be less than 1000 characters")
    .nullable(),
  pageDescription: z
    .string()
    .max(1000, "Page description must be less than 1000 characters")
    .nullable(),
  openGraphImage: z.string().nullable(),
  // NOTE (Noah, 2026-01-14, REPL-22251): Inverted logic - UI shows "Show in SEO" (positive)
  // but DB stores noIndex (negative). true = show in search engines = noIndex:false
  showInSearchEngines: z.boolean(),
});

type PageSettings = z.infer<typeof basePageSettingsSchema>;

export default function PageSettingsPanel({ page }: { page: ReploFile }) {
  const { setPanelView } = useEditorStoreActions();
  const projectId = useCurrentProjectId();
  const { project } = useCurrentProjectContext();
  const defaultCustomDomain = useDefaultCustomDomain();
  const logEvent = useLogAnalytics();

  const {
    handleSubmit,
    setValue,
    formState: { isDirty, errors },
    watch,
    register,
    control,
    reset,
    getValues,
  } = useForm<PageSettings>({
    resolver: zodResolver(basePageSettingsSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      pageName: page.name,
      pageUrl: (page.pageRoute ?? "").trim(),
      pageTitle: page.pageMetadataTitle ?? "",
      pageDescription: page.pageMetadataDescription ?? "",
      openGraphImage: page.pageMetadataOpenGraphImageUrl,
      // Invert: noIndex=true means hide from search engines, so showInSearchEngines=false
      showInSearchEngines: !page.pageMetadataNoIndex,
    },
  });

  // Keep preview URL separate for display purposes
  const [imagePreview, setImagePreview] = React.useState<string | null>(null);
  const [isImageUploadLoading, setIsImageUploadLoading] = React.useState(false);
  const [selectedImageFile, setSelectedImageFile] = React.useState<File | null>(
    null,
  );
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const { mutate: updatePage, isPending: isSaving } =
    nextJSProjectRouter.updatePage.useMutation({
      onSuccess: () => {
        if (projectId) {
          void nextJSProjectUtils.getPage.invalidate({
            projectId: projectId!,
            id: page.id,
          });
        }

        const currentValues = getValues();
        reset(currentValues, { keepValues: true });

        toast.success({
          title: "Changes saved",
          description: "Republish the page to make the updates live.",
        });
      },
    });

  const { mutateAsync: generateSignedUrlForOpenGraphImage } =
    shopMetadataRouter.generateSignedUrlForOpenGraphImage.useMutation();

  const handleClose = () => {
    setPanelView("chat");
  };

  const handleDiscard = () => {
    reset({
      pageName: page.name,
      pageUrl: (page.pageRoute ?? "").trim(),
      pageTitle: page.pageMetadataTitle ?? "",
      pageDescription: page.pageMetadataDescription ?? "",
      openGraphImage: page.pageMetadataOpenGraphImageUrl,
      showInSearchEngines: !page.pageMetadataNoIndex,
    });
    if (imagePreview && imagePreview.startsWith("blob:")) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview(page.pageMetadataOpenGraphImageUrl);
    setSelectedImageFile(null);
  };

  const handleSave = async (pageSettings: PageSettings) => {
    if (!projectId) {
      return;
    }

    setIsImageUploadLoading(true);

    let openGraphImageUrl = pageSettings.openGraphImage;

    if (selectedImageFile) {
      const { signedUrl, publicUrl } = await generateSignedUrlForOpenGraphImage(
        {
          projectId,
          fileId: page.id,
          imageName: selectedImageFile.name,
          contentType: selectedImageFile.type,
          contentLength: selectedImageFile.size,
        },
      );

      try {
        const uploadResponse = await fetch(signedUrl, {
          method: "PUT",
          body: selectedImageFile,
          headers: {
            "Content-Type": selectedImageFile.type,
            "Content-Length": selectedImageFile.size.toString(),
          },
        });

        if (!uploadResponse.ok) {
          throw new OpenGraphImageUploadError({
            message: `Failed to upload image ${selectedImageFile.name} to Cloudflare`,
            additionalData: { status: uploadResponse.status },
          });
        }

        openGraphImageUrl = publicUrl;

        if (imagePreview && imagePreview.startsWith("blob:")) {
          URL.revokeObjectURL(imagePreview);
        }
        setImagePreview(publicUrl);
        setSelectedImageFile(null);
      } catch (error) {
        setIsImageUploadLoading(false);
        toast.error({
          title: "Failed to upload image",
          description: `Failed to upload image ${selectedImageFile.name}`,
        });

        throw new OpenGraphImageUploadError({
          message: `Failed to upload image ${selectedImageFile.name}: ${error instanceof Error ? error.message : "Unknown error"}`,
          additionalData: { originalError: error },
        });
      }
    }

    const isPageRenamed = pageSettings.pageName !== page.name;

    updatePage({
      projectId,
      id: page.id,
      updates: {
        newName: pageSettings.pageName,
        newPageRoute: pageSettings.pageUrl,
        // NOTE (Kevin 2025-09-15): We use "||" here instead of "??" because that way empty strings are treated as false and it will be removed from the request.
        newPageMetadataTitle: pageSettings.pageTitle || undefined,
        newPageMetadataDescription: pageSettings.pageDescription || undefined,
        newPageMetadataOpenGraphImageUrl:
          openGraphImageUrl === "PENDING_UPLOAD"
            ? undefined
            : openGraphImageUrl ?? undefined,
        // NOTE (Noah, 2026-01-14, REPL-22251): Invert the UI value for DB storage
        // showInSearchEngines=true means noIndex=false (page should be indexed)
        newPageMetadataNoIndex: !pageSettings.showInSearchEngines,
      },
    });

    if (isPageRenamed) {
      logEvent("page.rename", {
        origin: "page-settings",
      });
    }
    setIsImageUploadLoading(false);
  };

  const handleSetValue = (
    key: keyof PageSettings,
    value: PageSettings[keyof PageSettings],
  ) => {
    setValue(key, value, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleImageUpload = (file: File) => {
    const isValidFile =
      file.type.startsWith("image/") || /\.(png|jpe?g|webp)$/i.test(file.name);

    if (!isValidFile) {
      return;
    }

    // Just store the file and create preview - don't upload yet
    const objectUrl = URL.createObjectURL(file);
    setImagePreview(objectUrl);
    setSelectedImageFile(file);

    handleSetValue("openGraphImage", "PENDING_UPLOAD");
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleReplaceImage = () => {
    fileInputRef.current?.click();
  };

  React.useEffect(() => {
    if (page.pageMetadataOpenGraphImageUrl && !imagePreview) {
      setImagePreview(page.pageMetadataOpenGraphImageUrl);
    }

    return () => {
      if (imagePreview && imagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview, page.pageMetadataOpenGraphImageUrl]);

  return (
    <div className="flex h-full min-h-0 min-w-0 w-full max-w-full flex-col md:shadow-2xs md:border-0.5 md:border-border md:rounded-lg bg-white relative overflow-hidden">
      <div className="hidden md:flex items-center justify-between p-3 border-b-0.5 border-border">
        <h2 className="header-h5">Page Settings</h2>
        <Button icon variant="tertiary" size="base" onClick={handleClose}>
          <X />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        <div className="md:p-5 space-y-4 border-b-0.5 border-border min-w-0">
          <h4 className="font-semibold leading-6">General</h4>
          <LabeledControl
            label="Name"
            size="base"
            error={errors.pageName?.message}
          >
            <Input
              {...register("pageName")}
              placeholder="Enter page name"
              size="base"
              invalid={Boolean(errors.pageName)}
            />
          </LabeledControl>
          <LabeledControl
            label="Path"
            size="base"
            error={errors.pageUrl?.message}
          >
            <Input
              {...register("pageUrl")}
              placeholder="Enter path"
              size="base"
              invalid={Boolean(errors.pageUrl)}
            />
          </LabeledControl>
        </div>
        <div className="pt-5 md:p-5">
          <div className="max-w-full">
            <h4 className="font-semibold leading-6 mb-4">SEO</h4>
            <div className="space-y-4 min-w-0">
              <LabeledControl
                label="Page Title"
                size="base"
                error={errors.pageTitle?.message}
              >
                <Input
                  {...register("pageTitle")}
                  placeholder="Enter page title"
                  size="base"
                  invalid={Boolean(errors.pageTitle)}
                />
              </LabeledControl>
              <LabeledControl
                label="Page Description"
                size="base"
                error={errors.pageDescription?.message}
              >
                <Controller
                  name="pageDescription"
                  control={control}
                  render={({ field }) => (
                    <Textarea
                      value={field.value ?? ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      placeholder="Enter page description"
                      size="base"
                      variant="long"
                      className="min-w-0"
                    />
                  )}
                />
              </LabeledControl>
              <LabeledControl label="Social Preview Image" size="base">
                <div>
                  {watch("openGraphImage") && (
                    <div className="flex gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleReplaceImage}
                      >
                        Replace
                      </Button>
                    </div>
                  )}
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileInputChange}
                  className="hidden"
                />

                {watch("openGraphImage") ? (
                  <div className="mt-1 flex items-start w-full h-19 border-0.5 border-border rounded-lg p-3 gap-3">
                    <div className="h-full aspect-[1200/630] rounded overflow-hidden flex-shrink-0">
                      <img
                        src={imagePreview ?? watch("openGraphImage") ?? ""}
                        alt="Uploaded preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-default font-medium truncate">
                        {selectedImageFile?.name ??
                          (imagePreview ?? watch("openGraphImage") ?? "")
                            .split("/")
                            .pop()}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    className="mt-1 flex items-center w-full h-19 border-1 border-component-border border-dashed rounded-lg bg-sheet p-3 gap-3 cursor-pointer hover:bg-surface transition-colors"
                    onClick={handleUploadClick}
                  >
                    <img
                      src="/images/chat/ImageBox.svg"
                      alt="Upload placeholder"
                      className="w-[42px] h-[42px] flex-shrink-0"
                    />
                    <div className="flex-1">
                      <div className="text-xs text-muted font-medium leading-4">
                        Upload an image
                      </div>
                      <div className="text-xs text-muted font-normal leading-4">
                        1200×630 pixels • 20MB max
                      </div>
                    </div>
                  </div>
                )}
              </LabeledControl>
              <LabeledControl label="Search Results Preview" size="base">
                <div className="p-3 border-0.5 border-border rounded-lg bg-white">
                  {watch("openGraphImage") && imagePreview && (
                    <div className="w-full aspect-[1200/630] rounded overflow-hidden mb-2">
                      <img
                        src={imagePreview}
                        alt="Social preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="text-primary text-sm font-medium leading-6 break-words">
                    {watch("pageTitle")}
                  </div>
                  <div className="text-muted text-xs leading-5 mt-1 break-words">
                    {watch("pageDescription")}
                  </div>
                  <div className="flex items-center gap-2 text-muted text-xs leading-5 mt-1">
                    <img
                      src={
                        project?.shopMetadataLightModeFaviconUrl ??
                        project?.shopMetadataDarkModeFaviconUrl ??
                        "/favicons/favicon-light-32x32.png"
                      }
                      alt="Site favicon"
                      className="w-4 h-4 flex-shrink-0 object-contain"
                    />
                    {defaultCustomDomain?.value ??
                      (project?.shopDomainPrefix
                        ? `${project.shopDomainPrefix}.reploshops.com`
                        : "your-store.reploshops.com")}
                  </div>
                </div>
              </LabeledControl>
              <Controller
                name="showInSearchEngines"
                control={control}
                render={({ field }) => (
                  <SelectionItem
                    type="switch"
                    variant="outline"
                    id="showInSearchEngines"
                    label="Show Page in SEO"
                    description="Shows page in search engines like Google. Republish for changes to take effect."
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                )}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row justify-end gap-3 pt-5 md:p-5 border-t-0.5 border-border">
        <Button
          variant="secondary"
          size="base"
          onClick={handleDiscard}
          disabled={!isDirty}
          className="w-full md:w-fit"
        >
          Discard
        </Button>
        <Button
          variant="primary"
          size="base"
          onClick={() => void handleSubmit(handleSave)()}
          disabled={
            !isDirty ||
            Object.keys(errors).length > 0 ||
            isSaving ||
            isImageUploadLoading
          }
          loading={isSaving || isImageUploadLoading}
          className="w-full md:w-fit"
        >
          Save Changes
        </Button>
      </div>
    </div>
  );
}
